import { useEffect } from 'react';
import { Flex, Paper, Radio, Switch, Text } from '@mantine/core';
import { StepCard } from 'src/UI/generate-flow/StepCard';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SelectableList } from 'src/UI/generate-flow/SelectableList';
import { MeetingNoteStepProps } from '../types';

export function NoteTemplateStep({ data, setData }: MeetingNoteStepProps) {
  const { files: templates, isLoading } = useTemplates({ templateType: 'MEETING_NOTE' });
  const { flagEnabledMeetingNoteSummarisation } = useFlags();

  useEffect(() => {
    if (!data.template && templates.length > 0) {
      const defaultTemplate = templates.find(t => t.isDefault);
      if (defaultTemplate) {
        setData(prev => ({ ...prev, template: defaultTemplate }));
      }
    }
  }, [templates, data.template, setData]);

  if (isLoading) return null;

  return (
    <Flex direction="column" h="100%" mih={0}>
      <StepCard title="Select a template for the meeting note">
        <Flex direction="column" gap="md">
          <SelectableList
            items={templates}
            getKey={t => t.id}
            renderRow={template => (
              <Paper
                radius="12"
                withBorder
                px="md"
                py="sm"
                role="button"
                tabIndex={0}
                onClick={() => setData(prev => ({ ...prev, template }))}
                styles={theme => ({
                  root: {
                    cursor: 'pointer',
                    borderColor:
                      data.template?.id === template.id
                        ? theme.colors.brand[6]
                        : 'var(--default-border-color)',
                    boxShadow: 'none',
                    userSelect: 'none',
                  },
                })}
              >
                <Flex align="center" gap="md">
                  <Radio
                    checked={data.template?.id === template.id}
                    onChange={() => setData(prev => ({ ...prev, template }))}
                    color="brand"
                    size="md"
                    iconColor="white"
                  />
                  <Text fw={FONT_WEIGHTS.semibold} size="sm" c="gray.8">
                    {template.displayName}
                  </Text>
                </Flex>
              </Paper>
            )}
          />
          {flagEnabledMeetingNoteSummarisation && (
            <Flex justify="space-between" align="center" px="xs">
              <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
                Summarise meeting note
              </Text>
              <Switch
                checked={data.summarise}
                onChange={event =>
                  setData(prev => ({ ...prev, summarise: event.currentTarget.checked }))
                }
              />
            </Flex>
          )}
        </Flex>
      </StepCard>
    </Flex>
  );
}
